import { equals } from '@unimpaired/utils'
import { AnimatePresence, motion } from 'framer-motion'
import * as R from 'ramda'
import React, { useEffect, useState } from 'react'
import ReactDOM from 'react-dom'
import useMeasure, { RectReadOnly } from 'react-use-measure'
import { twMerge } from 'tailwind-merge'
import { useClickOutside, useKeybind } from '../../hooks'

export type MenuProps<T = HTMLElement> = {
  isOpen: boolean
  onClose: () => void
  menuWidth?: 'auto' | 'match-trigger'
  className?: string
  children?: React.ReactNode
  triggerRef?: React.MutableRefObject<T | undefined | null>
  position?: 'top' | 'bottom' | 'left' | 'right'
  align?: 'start' | 'center' | 'end'
  sideOffset?: number
  closeOnScroll?: boolean
  closeOnClickOutside?: boolean
  disablePointerEvents?: boolean
}

export function Menu<T extends HTMLElement>({
  isOpen,
  menuWidth = 'auto',
  position = 'bottom',
  align = 'center',
  sideOffset = 4,
  closeOnScroll = true,
  closeOnClickOutside = true,
  disablePointerEvents,
  ...props
}: MenuProps<T>) {
  // A ref to a hidden menu that is only used to get the unconstrained
  // dimensions of the menu content
  const [hiddenMenuRef, menuBounds] = useMeasure({ scroll: true })
  // Once the hidden menu has been measured, some bounds will exists
  // and it's safe to draw the actual menu without a FOUC
  const menuHasBeenMeasured = Object.values(menuBounds).some(Boolean)
  // We need the bounds of the trigger so we can constrain the menu
  // layout in the case that the menu is set to match-trigger
  const [triggerBounds, setTriggerBounds] = useState(unsetPositionState)
  const triggerHasBeenMeasured = Object.values(triggerBounds).some(Boolean)

  // A state value for the position at which the menu should be drawn
  const [menuPosition, setMenuPosition] = React.useState(unsetPositionState)

  // We reset position so it's easy to determine if the menu
  // has actually been fully rendered, measured,
  // and is ready for display and interaction
  const onClose = () => {
    setMenuPosition(unsetPositionState)
    setTriggerBounds(unsetPositionState)
    props.onClose()
  }

  // If the menu is closed from the parent, we need to reset the position
  // so that our useClickOutside gets disabled, otherwise we end up
  // with the next click on the triggering element not openeing the menu
  useEffect(() => {
    if (!isOpen) {
      setMenuPosition(unsetPositionState)
      setTriggerBounds(unsetPositionState)
    }
  }, [isOpen])

  // We attach a ref to our actual interactive menu and close it
  // when it's clicked outside of the element.
  // This click outside is disabled when the menu is not set to open
  // or has not actually opened (i.e. the !Object.val...)
  const menuRef = useClickOutside<HTMLDivElement>(onClose, {
    disableWhen:
      !closeOnClickOutside ||
      !isOpen ||
      !Object.values(menuPosition).some(Boolean),
  })

  const recalculatePosition = () => {
    if (!isOpen || !menuHasBeenMeasured || !triggerHasBeenMeasured) return

    setMenuPosition(
      // @ts-expect-error TODO: STRICT MODE CHANGES
      getMenuPosition({
        position,
        align,
        sideOffset,
        triggerBounds,
        menuBounds,
        menuWidth,
      }),
    )
  }

  // Reset position on close so it's easy to tell if it's been drawn
  // in the right spot yet on next render
  const handleClose = (e: Event) => {
    if (e.target === menuRef.current) return
    onClose()
  }
  useEffect(() => {
    // use capture phase (third arg = true) to fire for scroll on any element
    closeOnScroll
      ? document.addEventListener('scroll', handleClose, true)
      : document.removeEventListener('scroll', handleClose)
    return () => document.removeEventListener('scroll', handleClose)
  }, [isOpen, closeOnScroll])

  useEffect(recalculatePosition, [triggerBounds, menuBounds])

  // We need to set up a mutation observer to track the trigger position
  // React use measure is insufficient because it will not update the bounds
  // if the element shifts, but doesn't change size, which happens not
  // infrequently with trigger type DOM nodes, specifically e.g. if a select
  // goes to an error state while the menu is open, the select button will
  // sometimes shift up in a flex container, but not actually change size
  useEffect(() => {
    const observer = new MutationObserver(() => {
      const newTriggerRect = props.triggerRef?.current?.getBoundingClientRect()
      if (!newTriggerRect) return

      const newTriggerPosition = R.omit(['toJSON'], newTriggerRect)

      if (!equals(newTriggerPosition, triggerBounds))
        setTriggerBounds(newTriggerPosition)
    })

    observer.observe(document.body, {
      attributes: true,
      childList: true,
      subtree: true,
    })
    return () => observer.disconnect()
  }, [])

  // Gate on isOpen so closed-but-mounted Menus don't sit on the LIFO Escape
  // stack and steal the keypress from whoever is actually visible.
  useKeybind(['Escape'], onClose, undefined, !isOpen)

  return ReactDOM.createPortal(
    <>
      {/* We need a instance of the children that is not constrained / allowed
          to conform to the parent container, so that if the trigger/menu changes
          then we can remeasure accurately rather than measuring the menu that
          may have grown. If we try to do this with the regular displayed menu, we can't
          accurately measure the unconstrained size of the menu content once it has been
          drawn and allowed to grow to it's parent's size. We effectively wouldn't
          otherwise be able to respond to menu content size changes */}
      {isOpen && (
        <div
          style={{
            position: 'absolute',
            width: undefined,
            height: undefined,
            visibility: 'hidden',
            left: '-9999px',
            top: '-9999px',
            pointerEvents: 'none',
          }}>
          <div
            ref={hiddenMenuRef}
            className={twMerge(
              `border bg-base-0 border-base-200 shadow-sm rounded-md p-2`,
              props.className,
            )}>
            {props.children}
          </div>
        </div>
      )}
      <AnimatePresence>
        {/* Make sure menuPosition is measured / defined to prevent flash of
                unstyle content */}
        {isOpen && Object.values(menuPosition).some(Boolean) && (
          <motion.div
            aria-expanded={isOpen}
            className={twMerge(
              'fixed z-120 origin-top',
              disablePointerEvents && 'pointer-events-none',
            )}
            initial={{
              scale: 1,
              translateY: -10,
              opacity: 0,
            }}
            animate={{
              translateY: 0,
              opacity: 1,
            }}
            exit={{
              opacity: 0,
            }}
            transition={{
              duration: 0.1,
            }}
            style={menuPosition}>
            <div
              className={twMerge(
                `border bg-base-0 border-base-200 shadow-sm rounded-md p-2`,
                props.className,
              )}
              ref={menuRef}>
              {props.children}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>,
    document.body,
  )
}

const unsetPositionState: RectReadOnly = {
  top: 0,
  left: 0,
  width: 0,
  height: 0,
  bottom: 0,
  right: 0,
  x: 0,
  y: 0,
}

const getMenuPosition = ({
  position = 'bottom',
  align = 'center',
  sideOffset,
  triggerBounds,
  menuBounds,
  menuWidth,
}: {
  position: MenuProps['position']
  align: MenuProps['align']
  sideOffset: number
  triggerBounds: RectReadOnly
  menuBounds: RectReadOnly
  menuWidth: MenuProps['menuWidth']
}) => {
  type Positions = typeof position

  const menuContentWiderThanTrigger = () => {
    if (!menuBounds.width) return undefined
    return menuBounds.width > triggerBounds.width
  }

  const adjustedAlign = !['top', 'bottom'].includes(position)
    ? align
    : menuContentWiderThanTrigger()
    ? 'center'
    : align

  const getTop = (position: Positions) => {
    if (!triggerBounds || !menuBounds) return undefined
    if (position === 'top') {
      return triggerBounds.top - menuBounds.height - sideOffset
    }
    if (position === 'bottom') {
      return triggerBounds.bottom + sideOffset
    }
    if (position === 'left' || position === 'right') {
      return adjustedAlign === 'center'
        ? triggerBounds.top + triggerBounds.height / 2 - menuBounds.height / 2
        : adjustedAlign === 'start'
        ? triggerBounds.top
        : triggerBounds.bottom - menuBounds.height
    }
  }

  type Coords = { x?: number; y: number } | { x: number; y?: number }
  const offScreen = ({ x, y }: Coords) => {
    if (!window) return true
    if (x) return x < 0 || x > window.innerWidth
    if (y) return y < 0 || y > window.innerHeight
    return undefined
  }

  const getSafeTop = (position: Positions) => {
    const nominalTop = getTop(position)
    if (!nominalTop) return undefined
    const height = getHeight()
    if (!height) return undefined

    if (position === 'bottom' && offScreen({ y: nominalTop + height })) {
      const inversePositionTop = getTop('top')
      if (!inversePositionTop) return undefined
      if (
        offScreen({ y: inversePositionTop }) &&
        !offScreen({ y: inversePositionTop + height })
      )
        return window.innerHeight - height
      return inversePositionTop
    }
    if (position === 'top' && offScreen({ y: nominalTop })) {
      const inversePositionTop = getTop('bottom')
      if (!inversePositionTop) return undefined
      if (
        offScreen({ y: inversePositionTop + height }) &&
        !offScreen({ y: inversePositionTop })
      )
        return 0
      return inversePositionTop
    }
    if (position === 'left' || position === 'right') {
      if (offScreen({ y: nominalTop })) return 0
      if (offScreen({ y: nominalTop + height }))
        return window.innerHeight - height
    }
    return nominalTop
  }

  const getWidth = () => {
    const nominalWidth =
      menuWidth === 'auto' ? menuBounds.width || undefined : triggerBounds.width
    const adjustedWidth = menuContentWiderThanTrigger()
      ? menuBounds.width || undefined
      : nominalWidth
    if (!adjustedWidth) return undefined
    return adjustedWidth > window.innerWidth ? window.innerWidth : adjustedWidth
  }

  const getLeft = (position: Positions) => {
    const width = getWidth() ?? 0
    if (!triggerBounds || !width) return undefined
    if (position === 'left') {
      return triggerBounds.left - width - sideOffset
    }
    if (position === 'right') {
      return triggerBounds.right + sideOffset
    }
    if (position === 'top' || position === 'bottom') {
      return adjustedAlign === 'center'
        ? triggerBounds.left + triggerBounds.width / 2 - width / 2
        : adjustedAlign === 'start'
        ? triggerBounds.left
        : triggerBounds.right - width
    }
  }

  const getSafeLeft = (position: Positions) => {
    const nominalLeft = getLeft(position)
    if (!nominalLeft) return undefined
    const width = getWidth()
    if (!width) return undefined

    if (position === 'right' && offScreen({ x: nominalLeft + width })) {
      const inversePositionLeft = getLeft('left')
      if (!inversePositionLeft) return undefined
      if (offScreen({ x: inversePositionLeft }))
        return window.innerWidth - width
      return inversePositionLeft
    }
    if (position === 'left' && offScreen({ x: nominalLeft })) {
      const inversePositionLeft = getLeft('right')
      if (!inversePositionLeft) return undefined
      if (offScreen({ x: inversePositionLeft + width })) return 0
      return inversePositionLeft
    }
    if (position === 'top' || position === 'bottom') {
      if (offScreen({ x: nominalLeft })) return 0
      if (offScreen({ x: nominalLeft + width }))
        return window.innerWidth - width
    }
    return nominalLeft
  }

  const getHeight = () => {
    const nominalHeight = menuBounds.height || undefined
    if (!nominalHeight) return undefined
    return nominalHeight > window.innerHeight
      ? window.innerHeight
      : nominalHeight
  }

  return {
    top: getSafeTop(position),
    left: getSafeLeft(position),
    width: getWidth() || 'auto',
    height: getHeight() || 'auto',
  }
}
